/**
 * The horizontal rail of status cards.
 *
 * From docs/references/17-updates-top.png. These are tall cards, not the
 * circles the old design used — the status image fills the card and the
 * name sits over it, so you choose what to open by seeing it rather than by
 * reading a list of names.
 *
 * The ring around each avatar is the unseen marker, the one piece of the
 * circle design that survived.
 */

import { groupByAuthor } from '@zivapp/shared'
import Avatar from '../ui/Avatar.jsx'
import './StatusRail.css'

/**
 * A card with no image needs a background of its own, and it has to be
 * stable: the same person must not change colour between renders.
 */
function tintFor (rawKey) {
  // null, not just undefined — see Avatar.
  const key = rawKey == null ? '' : String(rawKey)
  let hash = 0
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0
  const hue = [205, 262, 330, 22, 150, 190][Math.abs(hash) % 6]
  return `linear-gradient(170deg, hsl(${hue} 30% 38%), hsl(${hue} 34% 22%))`
}

function Card ({ name, avatarUrl, mediaUrl, unseen, mine, onClick }) {
  return (
    <button
      type="button"
      className={`sr__card${mine ? ' sr__card--mine' : ''}`}
      data-testid={mine ? 'my-status' : 'their-status'}
      style={mediaUrl ? undefined : { backgroundImage: tintFor(name) }}
      onClick={onClick}
    >
      {mediaUrl && <img className="sr__media" src={mediaUrl} alt="" />}

      <span className={`sr__ring${unseen ? ' is-unseen' : ''}`}>
        <Avatar name={name} url={avatarUrl} size={38} />
        {mine && (
          <span className="sr__plus" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="3.2"
                strokeLinecap="round" />
            </svg>
          </span>
        )}
      </span>

      <span className="sr__name">{name}</span>
    </button>
  )
}

export default function StatusRail ({ mine = [], feed = [], me, onAdd, onOpen }) {
  // One card per person, not per status: someone who posted six times today
  // is one entry in the rail, as the reference shows. groupByAuthor returns
  // an array of { userId, updates, allViewed, latestAt }, newest author
  // first — already the order the rail wants.
  const others = groupByAuthor(feed)

  const latestMine = mine[mine.length - 1] ?? null

  return (
    <div className="sr">
      {/* Tapping My status opens the composer. Not one card here had an
          onClick, which is what "the status is not clicking to upload"
          was — the whole rail was decoration. */}
      <Card
        name="My status"
        avatarUrl={me?.avatarUrl}
        mediaUrl={latestMine?.mediaUrl}
        mine
        onClick={onAdd}
      />

      {others.map(entry => {
        const latest = entry.updates[entry.updates.length - 1]
        return (
          <Card
            key={entry.userId}
            // A status carries only its author's id. Resolving that to a
            // name needs the contacts service, which has no provider yet —
            // so the id is shown, because it is at least true.
            name={latest?.authorName ?? entry.userId}
            avatarUrl={latest?.authorAvatarUrl}
            mediaUrl={latest?.mediaUrl}
            unseen={!entry.allViewed}
            onClick={() => onOpen?.(entry)}
          />
        )
      })}

      {others.length === 0 && (
        <p className="sr__none">No status updates from your contacts.</p>
      )}
    </div>
  )
}
